import React from 'react';
import { RichHtml } from './RichHtml';
import type { Question } from '../../services/examApi';

interface Props {
  question: Question;
  /** 用户对该题的作答（choice=label；response=文本；FRQ=按 part.index 的对象） */
  userAnswer: any;
  /** 标准答案原始串（choice=单字母；填空可能是 "a|b" / "a or b"；FRQ 可能为空） */
  correct: string;
  isCorrect: boolean;
}

/** 把标准答案串拆成可接受形式（与后端 answerChecking 对齐） */
function parseAccepted(standard: string): string[] {
  if (!standard) return [];
  return String(standard)
    .split(/\s+or\s+/i)
    .flatMap((p) => p.split('|'))
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * 报告详情用的只读题目视图：展示题干/选项 + 用户作答 + 标准答案 + 对错。
 * 选择题高亮"用户所选"和"正确项"；填空/FRQ 以文本展示。
 */
export const QuestionReview: React.FC<Props> = ({ question, userAnswer, correct, isCorrect }) => {
  const accepted = parseAccepted(correct);
  const isChoice = question.type === 'choice' && !!question.options?.length;
  const isFrq = question.type === 'response' && (question.parts?.length ?? 0) > 0;

  return (
    <div className="space-y-5">
      {/* 结果条 */}
      <div className="flex items-center gap-3 text-sm">
        <span className={`px-2 py-0.5 rounded-full font-medium ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {isCorrect ? '✅ Correct' : '❌ Incorrect'}
        </span>
        {correct && (
          <span className="text-gray-500">
            Correct answer: <span className="font-semibold text-gray-800">{correct}</span>
          </span>
        )}
      </div>

      {question.passage?.html && (
        <RichHtml html={question.passage.html} className="prose max-w-none text-gray-800 leading-relaxed" />
      )}
      {question.question?.html && (
        <RichHtml html={question.question.html} className="prose max-w-none font-medium text-gray-900" />
      )}

      {/* 选择题：标注用户所选 + 正确项 */}
      {isChoice && (
        <div className="space-y-2">
          {question.options!.map((opt) => {
            const chosen = userAnswer === opt.label;
            const isRight = accepted.some((a) => a.toLowerCase() === opt.label.toLowerCase());
            const cls = isRight
              ? 'border-green-500 bg-green-50'
              : chosen
              ? 'border-red-500 bg-red-50'
              : 'border-gray-200';
            return (
              <div key={opt.label} className={`w-full text-left flex items-start gap-3 rounded-xl border px-4 py-3 ${cls}`}>
                <span
                  className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                    isRight ? 'bg-green-500 text-white' : chosen ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {opt.label}
                </span>
                <RichHtml html={opt.html} className="prose max-w-none flex-1 pt-0.5" />
                <span className="shrink-0 flex flex-col items-end gap-0.5 text-[11px]">
                  {chosen && <span className="text-red-600">Your answer</span>}
                  {isRight && <span className="text-green-600">Correct</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* 填空题（无 parts） */}
      {question.type === 'response' && !isFrq && (
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-gray-500">Your answer: </span>
            <span className="font-medium text-gray-900">{userAnswer ? String(userAnswer) : '—'}</span>
          </div>
          {accepted.length > 0 && (
            <div>
              <span className="text-gray-500">Accepted: </span>
              <span className="font-medium text-gray-800">{accepted.join(' , ')}</span>
            </div>
          )}
        </div>
      )}

      {/* FRQ（含 parts）：逐子问展示题面与用户作答 */}
      {isFrq && (
        <div className="space-y-5">
          {question.parts!.map((p) => (
            <div key={p.index} className="space-y-2">
              <div className="flex items-start gap-2">
                {p.label && <span className="shrink-0 font-semibold text-primary-700">{p.label}</span>}
                <RichHtml html={p.html} className="prose max-w-none flex-1" />
              </div>
              <div className="text-sm">
                <span className="text-gray-500">Your response: </span>
                <span className="font-medium text-gray-900 whitespace-pre-wrap">
                  {(userAnswer && userAnswer[p.index]) || '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
